// Margin summary — Blended margin as big orange 30px number.
// Extracted from QuoteBuilder.jsx (Wave K refactor).
import { TrendingUp, Tag } from "lucide-react";
import { GBP, websiteDiscount } from "../_quoteHelpers";
import { T2Panel } from "../_t2";

const SummaryRow = ({ label, value, tone = "white" }) => {
  const colorMap = {
    ok:    "var(--t2-ok-ink)",
    teal:  "var(--t2-teal-ink)",
    brass: "var(--t2-brass-ink)",
    white: "var(--t2-white)",
  };
  const color = colorMap[tone] || colorMap.white;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "6px 0" }}>
      <span style={{ color: "var(--t2-grey2)", fontFamily: "var(--t2-mono)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</span>
      <span className="t2-mono" style={{ color, fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );
};

export default function SummaryPanel({ quote, subtotal, delivery }) {
  // Hardcoded RWA45 branch deal (sourced from Talmia 1 supplier data): the
  // Rockwool RWA45 packs cost 5% less at the two London Galaxy branches, and
  // that 5% stacks WITH the standard Galaxy rebate (double-stack).
  const RWA45_BRANCH_SKUS = ["50300", "50302", "50303"];
  const RWA45_BRANCH_NAMES = ["galaxy - west london", "galaxy - east london"];
  const RWA45_BRANCH_PCT = 5;
  const supplierName = String(quote.Supplier?.name || "").trim().toLowerCase();
  const branchDealActive = RWA45_BRANCH_NAMES.includes(supplierName);
  const lineQualifies = (it) =>
    branchDealActive && RWA45_BRANCH_SKUS.some((n) => String(it.sku || "").includes(n));

  const totalCost   = (quote.LineItems || []).reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.qty) || 0), 0);
  // 5% off the qualifying RWA45 lines' cost — a real cost reduction applied
  // BEFORE the rebate so the two stack.
  const branchDiscAbs = (quote.LineItems || []).reduce(
    (s, it) => s + (lineQualifies(it) ? (Number(it.cost) || 0) * (Number(it.qty) || 0) * (RWA45_BRANCH_PCT / 100) : 0),
    0,
  );
  const adjustedCost = totalCost - branchDiscAbs;          // true purchase cost at branch
  // Rebate only ever applies to Galaxy branches (matches the Supplier control).
  const rebatePct   = quote.Supplier?.category === "Galaxy" ? (Number(quote.RebatePct) || 0) : 0;
  const rebateAbs   = adjustedCost * (rebatePct / 100);    // rebate stacks on the reduced cost
  const netCost     = adjustedCost - rebateAbs;
  const marginAbs   = subtotal - adjustedCost;
  const marginPct   = subtotal > 0 ? (marginAbs / subtotal) * 100 : 0;
  const realMargin  = subtotal - netCost;
  const realMarginPct = subtotal > 0 ? (realMargin / subtotal) * 100 : 0;
  const vat   = (subtotal + (delivery.cost || 0)) * 0.20;
  const grand = subtotal + (delivery.cost || 0) + vat;
  const tone  = realMarginPct >= 6 ? "ok" : realMarginPct >= 3 ? "warn" : "red";
  const web   = websiteDiscount(quote.LineItems || []);

  return (
    <T2Panel
      icon={TrendingUp}
      title="Margin summary"
      testid="panel-summary"
      right={<span className="t2-mono" style={{ color: `var(--t2-${tone}-ink)`, fontSize: 11, fontWeight: 700 }}>{realMarginPct.toFixed(1)}%</span>}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 14 }}>
        <span className="t2-blended-highlight">{realMarginPct.toFixed(1)}%</span>
        <span className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey2)" }}>
          {rebatePct > 0 ? "real margin (post-rebate)" : "blended margin"}
        </span>
      </div>
      <div>
        <SummaryRow label="Cost of goods (gross)" value={GBP(totalCost)} />
        {branchDiscAbs > 0 && (
          <SummaryRow label={`RWA45 branch · −${RWA45_BRANCH_PCT}%`} value={"-" + GBP(branchDiscAbs)} tone="teal" />
        )}
        {rebatePct > 0 && (
          <>
            <SummaryRow label={`Rebate · ${rebatePct}%`} value={"-" + GBP(rebateAbs)} tone="teal" />
            <SummaryRow label="Real cost (post-rebate)" value={GBP(netCost)} tone="brass" />
            <SummaryRow label="Margin before rebate" value={`${marginPct.toFixed(1)}%`} />
          </>
        )}
        <SummaryRow label="Products (ex VAT)" value={GBP(subtotal)} />
        <SummaryRow label="Delivery"          value={delivery.cost > 0 ? GBP(delivery.cost) : "FREE"} tone={delivery.cost > 0 ? "white" : "ok"} />
        <SummaryRow label="VAT 20%"           value={GBP(vat)} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--t2-border)" }}>
          <span style={{ color: "var(--t2-grey2)", fontFamily: "var(--t2-mono)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Total inc VAT</span>
          <span className="t2-mono" style={{ color: "var(--t2-orange-ink)", fontSize: 18, fontWeight: 800 }}>{GBP(grand)}</span>
        </div>
      </div>
      {web.count > 0 && <WebDiscount web={web} />}
    </T2Panel>
  );
}

// Staff-only: how much below the live website price this quote is.
function WebDiscount({ web }) {
  const off = web.disc > 0.005;
  const above = web.disc < -0.005;
  return (
    <div data-testid="web-discount-block" style={{
      marginTop: 14, padding: "10px 12px", borderRadius: 10,
      background: "color-mix(in srgb, var(--qb-disc) 9%, transparent)",
      border: "1px solid color-mix(in srgb, var(--qb-disc) 35%, transparent)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <Tag className="w-3.5 h-3.5" style={{ color: "var(--qb-disc)" }} />
        <span className="t2-mono" style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--qb-disc)" }}>Discount vs website</span>
        <span className="t2-mono" style={{ marginLeft: "auto", fontSize: 9, color: "var(--t2-grey3)" }}>staff only</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span className="t2-mono" data-testid="web-discount-pct" style={{ fontSize: 22, fontWeight: 800, color: "var(--qb-disc)" }}>
          {off ? `${web.pct.toFixed(1)}%` : above ? `+${Math.abs(web.pct).toFixed(1)}%` : "0%"}
        </span>
        <span className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey2)" }}>{off ? "off website price" : above ? "above website price" : "at website price"}</span>
      </div>
      <SummaryRow label="Website price (ex VAT)" value={GBP(web.web)} />
      <SummaryRow label={off ? "Customer saves" : "Difference"} value={<span data-testid="web-discount-value" style={{ color: "var(--qb-disc)" }}>{off ? "−" : above ? "+" : ""}{GBP(Math.abs(web.disc))}</span>} />
      <div className="t2-mono" style={{ fontSize: 9.5, color: "var(--t2-grey3)", marginTop: 2 }}>
        Based on {web.count} website-priced line{web.count === 1 ? "" : "s"} · use it when selling: "that's {GBP(Math.max(0, web.disc))} off our web price"
      </div>
    </div>
  );
}

